/**
 * PHYSICAL-RISK-CALCULATION-DETAILS
 * Explique en clair d'ou sort le risque, a cote de l'echelle de l'etape en cours : les deux
 * elements du calcul (Gravite), le risque brut qui s'en deduit (Frequence), puis le detail complet
 * du calcul (Evaluation, ou "prevention" pour l'echelle qui y est reglee).
 */
import { useState } from "react"
import Chip from "@/components/chip"
import DashboardNumber from "@/components/dashboard-number"
import Link from "@/components/link"
import { useAppSelector } from "@/core/root-reducer"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import { PhysicalRiskEvaluationType } from "@/entities/physical-risk/model/physical-risk.types"
import { formatRiskValue } from "@/entities/physical-risk/model/physical-risk.utils"
import { PhysicalRiskMeter } from "@/entities/physical-risk/ui/PhysicalRiskMeter"
import RiskWizardOverview from "@/entities/physical-risk/ui/RiskWizardOverview"
import PhysicalRiskEvaluationHelpModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEvaluationHelpModal"
import {
  getGradientColorAt,
  getPreventionColorAt,
  getScalePosition
} from "@/entities/physical-risk/ui/physical-risk-scale-colors.utils"
import { t } from "@/translate/t"

const METER_COLUMN_WIDTH = 115 //Largeur de la jauge : le niveau de risque s'aligne a sa droite

interface Props {
  onEditFrequency?: () => void //Corrige la frequence depuis son chip, sans passer par la sidebar
  onEditSeverity?: () => void //Idem pour la gravite
  step: PhysicalRiskEvaluationType
}

/** Compensation optique : les glyphes des operateurs sont centres sur la pastille, pas sur sa baseline. */
function FormulaOperator({ children }: { children: React.ReactNode }) {
  return <span className="grey-t flex h-[24px] -translate-y-[2px] items-center text-lg">{children}</span>
}

function CalculationValue({ children, color }: { children: React.ReactNode; color?: string }) {
  return (
    <div className="text-base font-bold" style={{ color }}>
      {children}
    </div>
  )
}

/** Un terme du calcul : le mot en chip a la couleur de son niveau, sa valeur dessous si demandee */
function FormulaTerm({
  color,
  isValueVisible,
  label,
  onClick,
  value
}: {
  color: string
  isValueVisible: boolean
  label: string
  onClick?: () => void
  value: number
}) {
  return (
    <div className="flex flex-col items-center gap-[5px]">
      <Chip color={color} onClick={onClick}>
        {label}
      </Chip>
      {isValueVisible && <CalculationValue color={color}>{value}</CalculationValue>}
    </div>
  )
}

/** Une puce, sans dependre du style par defaut des listes du navigateur */
function DetailBullet({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-10">
      <div className="shrink-0">•</div>
      <div>{children}</div>
    </div>
  )
}

export default function PhysicalRiskCalculationDetails({ onEditFrequency, onEditSeverity, step }: Props) {
  const draft = useAppSelector(physicalRiskStore.selectors.draft)
  const risksShowCalculations = useAppSelector((state) => state._session.userOptions.risksShowCalculations)
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const isIdtreeScoreHidden = accountRisksSettings.isIdtreeScoreHidden
  const [isCalculationHelpOpen, setIsCalculationHelpOpen] = useState(false)

  const showCalculations = risksShowCalculations

  //Couleurs des niveaux retenus, reprises du gradient de leur echelle
  const severityColor = getGradientColorAt(getScalePosition(draft.cSeverityScale, draft.severity))
  const frequencyColor = getGradientColorAt(getScalePosition(draft.cFrequencyScale, draft.frequency))
  const preventionColor = getPreventionColorAt(getScalePosition(draft.cPreventionScale, draft.prevention))

  const title = {
    frequency: t("physical_risk_calculation_gross_title"),
    prevention: "Calcul du risque résiduel",
    severity: "Comment le risque est-il calculé ?"
  }[step]

  const residualFormula = (
    <div className="flex items-start justify-center gap-10">
      <FormulaTerm
        color={severityColor}
        isValueVisible={showCalculations}
        label="G"
        value={draft.severity}
        onClick={onEditSeverity}
      />
      <FormulaOperator>×</FormulaOperator>
      <FormulaTerm
        color={frequencyColor}
        isValueVisible={showCalculations}
        label="F"
        value={draft.frequency}
        onClick={onEditFrequency}
      />
      <FormulaOperator>/</FormulaOperator>
      <FormulaTerm color={preventionColor} isValueVisible={showCalculations} label="P" value={draft.prevention} />
    </div>
  )

  const helpLink = (
    <Link isWithoutMargin onClick={() => setIsCalculationHelpOpen(true)}>
      Calcul de la note
    </Link>
  )

  //Sans note IDTree, la chip porte deja le niveau et le score : pas de nombre a cote
  const residualResult = isIdtreeScoreHidden ? (
    <div className="flex flex-col items-center gap-[5px]">
      <PhysicalRiskMeter risk={draft} size="lg" />
      {showCalculations && helpLink}
    </div>
  ) : (
    <div
      className="grid items-center justify-items-center gap-x-10 gap-y-[5px]"
      style={{ gridTemplateColumns: `${METER_COLUMN_WIDTH}px 1fr` }}>
      <div style={{ gridColumn: 1, gridRow: 1 }}>
        <PhysicalRiskMeter risk={draft} size="lg" />
      </div>
      <div style={{ gridColumn: 2, gridRow: 1 }}>
        <DashboardNumber
          color={draft.cValueEffectiveColor ?? undefined}
          count={draft.cValueEffectiveLabel}
          header="Risque résiduel"
          fontSize={18}
        />
      </div>
      {showCalculations && (
        <>
          <div style={{ gridColumn: 1, gridRow: 2 }}>{helpLink}</div>
          <div style={{ gridColumn: 2, gridRow: 2 }}>
            <CalculationValue color={draft.cValueEffectiveColor ?? undefined}>
              {formatRiskValue(draft.cValueEffective ?? 0)}
            </CalculationValue>
          </div>
        </>
      )}
    </div>
  )

  return (
    <>
      {isCalculationHelpOpen && (
        <PhysicalRiskEvaluationHelpModal
          accountRisksSettings={accountRisksSettings}
          risk={draft}
          onClose={() => setIsCalculationHelpOpen(false)}
        />
      )}

      {/* boxSizing : le reset Tailwind est desactive (preflight), la bordure et le padding
          s'ajouteraient a la largeur et pousseraient la carte sous l'echelle */}
      <aside
        className="ml-auto w-[280px] shrink-0 rounded-[12px] p-20"
        style={{
          border: "1px solid #E2E8F0",
          boxSizing: "border-box",
          paddingBottom: step === "frequency" ? 0 : undefined
        }}>
        <RiskWizardOverview risk={draft} />

        <div className="mb-20 grey-t text-center">
          <b>{title}</b>
        </div>

        {step === "severity" && (
          <div className="grey-t flex flex-col gap-10 text-sm leading-snug">
            <div>Le risque brut dépend de deux éléments&nbsp;:</div>
            <DetailBullet>
              <b>La gravité</b>, c&apos;est ce qui peut arriver de pire à une personne.
            </DetailBullet>
            <DetailBullet>
              <b>La fréquence</b>, c&apos;est le nombre de fois où les personnes sont exposées au danger.
            </DetailBullet>
            <div>Le niveau de risque est ensuite réduit en fonction des mesures de prévention mises en place.</div>
          </div>
        )}

        {step === "frequency" && (
          <div className="mt-40 flex flex-col items-center gap-20">
            <div className="flex items-start justify-center gap-10">
              <FormulaTerm
                color={severityColor}
                isValueVisible={showCalculations}
                label={t("physical_risk_calculation_severity")}
                value={draft.severity}
                onClick={onEditSeverity}
              />
              <FormulaOperator>×</FormulaOperator>
              <FormulaTerm
                color={frequencyColor}
                isValueVisible={showCalculations}
                label={t("physical_risk_calculation_frequency")}
                value={draft.frequency}
              />
            </div>

            <div className="flex w-[150px] flex-col items-center">
              <DashboardNumber
                color={draft.cValueInitialColor ?? undefined}
                count={draft.cValueInitialLabel}
                header={t("physical_risk_calculation_gross_risk")}
                fontSize={18}
              />
              {showCalculations && (
                <CalculationValue color={draft.cValueInitialColor ?? undefined}>
                  {formatRiskValue(draft.cValueInitial ?? 0)}
                </CalculationValue>
              )}
            </div>
          </div>
        )}

        {step === "prevention" && (
          <div className="mt-20 flex flex-col items-center gap-20">
            {residualResult}
            {/* Initiales : les trois termes et leurs operateurs ne tiennent pas en entier dans la carte */}
            {residualFormula}
          </div>
        )}
      </aside>
    </>
  )
}
